/**
 * Review links of an ad (docs/SPEC.md §14.5). The owner's side under
 * `/v1/ads/{id}/share` and `/v1/share-*`; the public side under `/v1/r/{token}`
 * with no session, which is why the review page never touches `/v1/me`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  CreateShareResponse,
  PublicReview,
  ReuseOffer,
  ShareComment,
  ShareIndex,
} from "./types";

export const shareKey = (adId: string) => ["share", adId] as const;
export const reviewKey = (token: string) => ["review", token] as const;
export const reuseKey = (token: string) => ["reuse", token] as const;

/** `GET /v1/ads/{id}/share`: the ad's links (no tokens) and comments. */
export function useShareIndex(adId: string, { enabled = true } = {}) {
  return useQuery({
    queryKey: shareKey(adId),
    queryFn: () => api<ShareIndex>(`/v1/ads/${encodeURIComponent(adId)}/share`),
    enabled,
  });
}

function useInvalidateShare(adId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: shareKey(adId) });
  };
}

/** `POST /v1/ads/{id}/share {expires_in_days?, allow_comments?, reuse?}` → 201 with
 *  the token and URL, shown once and never listed again. */
export function useCreateShareLink(adId: string) {
  const invalidate = useInvalidateShare(adId);
  return useMutation({
    mutationFn: (body: {
      expires_in_days: number;
      allow_comments: boolean;
      reuse: boolean;
    }) =>
      api<CreateShareResponse>(`/v1/ads/${encodeURIComponent(adId)}/share`, {
        method: "POST",
        body,
      }),
    onSettled: invalidate,
  });
}

/** `DELETE /v1/share-links/{id}` → 204: the link answers 404 from now on. */
export function useRevokeShareLink(adId: string) {
  const invalidate = useInvalidateShare(adId);
  return useMutation({
    mutationFn: (linkId: string) =>
      api<void>(`/v1/share-links/${encodeURIComponent(linkId)}`, {
        method: "DELETE",
      }),
    onSettled: invalidate,
  });
}

/** `DELETE /v1/share-comments/{id}` → 204: hidden for everyone. */
export function useHideShareComment(adId: string) {
  const invalidate = useInvalidateShare(adId);
  return useMutation({
    mutationFn: (commentId: string) =>
      api<void>(`/v1/share-comments/${encodeURIComponent(commentId)}`, {
        method: "DELETE",
      }),
    onSettled: invalidate,
  });
}

/** `GET /v1/r/{token}` (public, `no-store`): expired and revoked links are a
 *  404 with no distinction, so the page shows one sentence for both. */
export function usePublicReview(token: string) {
  return useQuery({
    queryKey: reviewKey(token),
    queryFn: () => api<PublicReview>(`/v1/r/${encodeURIComponent(token)}`),
    staleTime: 60_000,
  });
}

/** `POST /v1/r/{token}/comments {author_name, body, t}` → 201 comment; 429
 *  `rate_limited` past 30 per link per hour or 3 per IP per minute. */
export function usePostPublicComment(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      author_name: string;
      body: string;
      t: number | null;
    }) =>
      api<ShareComment>(`/v1/r/${encodeURIComponent(token)}/comments`, {
        method: "POST",
        body,
      }),
    onSuccess: (comment) => {
      queryClient.setQueryData<PublicReview>(reviewKey(token), (old) =>
        old && !old.comments.some((c) => c.id === comment.id)
          ? { ...old, comments: [...old.comments, comment] }
          : old,
      );
    },
  });
}

/** `GET /v1/r/{token}/reuse` (signed in): the shared film's scenes and its
 *  templates. 401 when nobody is signed in, 404 when the link does not allow
 *  reuse (P27-T01). */
export function useReuseOffer(token: string, { enabled = true } = {}) {
  return useQuery({
    queryKey: reuseKey(token),
    queryFn: () => api<ReuseOffer>(`/v1/r/${encodeURIComponent(token)}/reuse`),
    enabled,
    retry: false,
  });
}

/** `POST /v1/r/{token}/template {template_id}` → 201: the template copied
 *  into the person's organisation, as `GET /v1/templates` lists it. */
export function useCopySharedTemplate(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      api<ReuseOffer["templates"][number]>(
        `/v1/r/${encodeURIComponent(token)}/template`,
        { method: "POST", body: { template_id: templateId } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

/** `POST /v1/r/{token}/scene {ad_id, scene_id}` → 202: a change on the
 *  person's ad that takes the scene; the ad's page follows it. */
export function useTakeSharedScene(token: string) {
  return useMutation({
    mutationFn: (body: { ad_id: string; scene_id: string }) =>
      api<{ ad_id: string; id: string; n: number; state: string }>(
        `/v1/r/${encodeURIComponent(token)}/scene`,
        { method: "POST", body },
      ),
  });
}
